import { registerTool, unregisterTool } from '@sazeo/claude';
import type { ComponentType } from 'react';
import type {
  AddonDashboardWidget,
  AddonEntityTab,
  AddonInlineAction,
  AddonMenuEntry,
} from './manifest';
import type { AddonRegistry } from './registry';

/**
 * Mount surface — the flattened extension-point output that the host shell
 * actually renders. Nothing in here is addon-scoped state; the shell treats
 * this as plain data.
 */

export interface MenuEntry extends AddonMenuEntry {
  /** addon id — lets the shell key routes by addon ownership. */
  addonId: string;
}

export interface DashboardWidget extends AddonDashboardWidget {
  addonId: string;
  /** Resolved React component, looked up from `AddonCode.components`. */
  Component: ComponentType<Record<string, unknown>>;
}

export interface EntityTab extends AddonEntityTab {
  addonId: string;
  Component: ComponentType<Record<string, unknown>>;
}

export interface InlineAction extends AddonInlineAction {
  addonId: string;
  /** Resolved handler function, looked up from `AddonCode.handlers`. */
  run: (...args: unknown[]) => Promise<unknown>;
}

export interface MountedAddons {
  menuEntries: MenuEntry[];
  dashboardWidgets: DashboardWidget[];
  entityTabs: Record<string, EntityTab[]>;
  inlineActions: Record<string, InlineAction[]>;
  /**
   * Names of Claude tools that were successfully registered. The unmount
   * step uses this to call `unregisterTool()` cleanly without leaking
   * registrations between tenant switches.
   */
  registeredClaudeToolNames: string[];
}

/**
 * Optional context passed to `mountExtensionPoints` so pure filters (e.g.
 * `menu_entry.requires_role`) can run without the shell doing a second pass.
 */
export interface MountContext {
  /** Role names the caller holds in the active tenant (e.g. ['member']). */
  userRoles?: readonly string[];
}

/**
 * Walk every addon enabled for `tenantId` and produce a flat, sorted view of
 * its extension points. Also registers any declared Claude tools with the
 * `@sazeo/claude` in-memory registry.
 *
 * Missing components/handlers are warned but do not throw — a malformed
 * addon manifest should never crash the whole shell at boot.
 *
 * When `ctx.userRoles` is supplied, `menu_entry.requires_role` is enforced
 * client-side (the backend still enforces authz via RLS; this is UX hygiene).
 */
export function mountExtensionPoints(
  registry: AddonRegistry,
  tenantId: string,
  ctx: MountContext = {},
): MountedAddons {
  const menuEntries: MenuEntry[] = [];
  const dashboardWidgets: DashboardWidget[] = [];
  const entityTabs: Record<string, EntityTab[]> = {};
  const inlineActions: Record<string, InlineAction[]> = {};
  const registeredClaudeToolNames: string[] = [];

  const enabledManifests = registry.listEnabledForTenant(tenantId);
  for (const manifest of enabledManifests) {
    const entry = registry.getEntry(manifest.id);
    if (!entry) continue;
    const { code } = entry;

    const ep = manifest.extension_points;

    if (ep.menu_entry) {
      const required = ep.menu_entry.requires_role;
      const passesRole =
        !required ||
        required.length === 0 ||
        (ctx.userRoles ?? []).some((r) => required.includes(r));
      if (passesRole) {
        menuEntries.push({ ...ep.menu_entry, addonId: manifest.id });
      }
    }

    if (ep.dashboard_widgets) {
      for (const widget of ep.dashboard_widgets) {
        const Component = code.components[widget.component];
        if (!Component) {
          console.warn(
            `[addon-runtime] addon "${manifest.id}" declares widget "${widget.id}" → component "${widget.component}" but no such component was registered`,
          );
          continue;
        }
        dashboardWidgets.push({ ...widget, addonId: manifest.id, Component });
      }
    }

    if (ep.entity_tabs) {
      for (const tab of ep.entity_tabs) {
        const Component = code.components[tab.component];
        if (!Component) {
          console.warn(
            `[addon-runtime] addon "${manifest.id}" declares tab on "${tab.entity}" → component "${tab.component}" but no such component was registered`,
          );
          continue;
        }
        const bucket = entityTabs[tab.entity] ?? [];
        bucket.push({ ...tab, addonId: manifest.id, Component });
        entityTabs[tab.entity] = bucket;
      }
    }

    if (ep.inline_actions) {
      for (const action of ep.inline_actions) {
        const run = code.handlers[action.handler];
        if (!run) {
          console.warn(
            `[addon-runtime] addon "${manifest.id}" declares action "${action.action}" on "${action.entity}" → handler "${action.handler}" but no such handler was registered`,
          );
          continue;
        }
        const bucket = inlineActions[action.entity] ?? [];
        bucket.push({ ...action, addonId: manifest.id, run });
        inlineActions[action.entity] = bucket;
      }
    }

    if (manifest.claude_tools && code.claudeTools) {
      const toolsByName = new Map<string, (typeof code.claudeTools)[number]>();
      for (const tool of code.claudeTools) {
        toolsByName.set(tool.name, tool);
      }
      for (const ref of manifest.claude_tools) {
        const tool = toolsByName.get(ref.name);
        if (!tool) {
          console.warn(
            `[addon-runtime] addon "${manifest.id}" declares claude tool "${ref.name}" but no implementation was provided`,
          );
          continue;
        }
        registerTool(tool);
        registeredClaudeToolNames.push(tool.name);
      }
    }
  }

  // Menu entries are rendered by the shell in order; sort once here so the
  // shell never has to re-sort on re-render.
  menuEntries.sort((a, b) => a.order - b.order);

  // Remember what we registered so a later `unmountExtensionPoints(registry)`
  // can tear down exactly those tools without needing the caller to hold on
  // to the mounted result.
  registry.__setMountedClaudeToolNames(registeredClaudeToolNames);

  return {
    menuEntries,
    dashboardWidgets,
    entityTabs,
    inlineActions,
    registeredClaudeToolNames,
  };
}

/**
 * Reverse of `mountExtensionPoints`: clears the Claude tools that were
 * registered by the last mount for this registry. Call this on tenant
 * switch or sign-out.
 *
 * Only unregisters names this registry's last mount owned — we never trample
 * tools a different addon-runtime instance registered.
 */
export function unmountExtensionPoints(registry: AddonRegistry): void {
  for (const name of registry.__getMountedClaudeToolNames()) {
    unregisterTool(name);
  }
  registry.__setMountedClaudeToolNames([]);
}
